import { LitElement, type CSSResultGroup, type CSSResultOrNative } from 'lit'
import { SignalWatcher, type SignalWatcherApi } from '@lit-labs/signals'
import { BaseElement, type IBaseMixin } from './baseElement'
import { tailwindStyles } from './tailwind.mixin'
import type { Constructor } from './constructor'
import { _activeHost } from '@schmancy/state/active-host'

// Cast to instance-only Constructor + a narrow static surface for `finalizeStyles`.
// Avoids exposing LitElement's full static side (which has `protected static
// shadowRootOptions`); that would conflict with subclasses that declare their own
// `protected static shadowRootOptions = { ..., delegatesFocus: true }` to widen
// visibility — same shape TailwindElement uses.
type StaticFinalizeStyles = {
	finalizeStyles(styles?: CSSResultGroup): CSSResultOrNative[]
}
const SchmancyElementBase = SignalWatcher(BaseElement(LitElement)) as unknown as CustomElementConstructor &
	Constructor<LitElement> &
	Constructor<IBaseMixin> &
	Constructor<SignalWatcherApi> &
	StaticFinalizeStyles

// ---------------------------------------------------------------------------
// Active-host integration. Two layers:
//
//   (1) Prototype-chain wrap. The first instance of every concrete subclass
//       triggers a walk that wraps every function-typed descriptor on the
//       chain (up to but not including HTMLElement.prototype) with
//       `_activeHost.run(this, …)`. Catches `render()`, every Lit lifecycle
//       hook, and every user-defined class method (sync or async — the host
//       propagates across `await` via the Promise.then patch in
//       `active-host.ts`). Idempotent: each prototype object is wrapped at
//       most once, regardless of how many subclasses share it.
//
//   (2) `addEventListener` override. Listeners attached imperatively to
//       the host run inside `_activeHost.run(this, …)`. We keep an
//       `original→wrapped` map so `removeEventListener` finds the wrapped
//       listener.
//
// Inline arrow handlers attached via Lit templates (`@click=${() => …}`)
// are not wrapped at this layer — Lit attaches them to *child* elements,
// not the host. They resolve correctly via the
// `window.event.composedPath()` fallback in `resolveActiveHost()`: a
// `context-request` dispatched from the click target bubbles (composed)
// up to the enclosing `<schmancy-context>` provider.
// ---------------------------------------------------------------------------

// Lit's internal protocol methods (e.g. `_$attributeToProperty`) are
// framework plumbing; wrapping them risks breaking Lit's update scheduling
// for no semantic gain — the host is already on the stack from the wrap of
// the public lifecycle methods that call them.
function shouldSkipMethod(key: string): boolean {
	if (key === 'constructor') return true
	if (key.startsWith('_$')) return true
	return false
}

const wrappedProtos = new WeakSet<object>()

function wrapPrototypeChain(ctor: { prototype: object }): void {
	let proto: object | null = ctor.prototype
	while (proto && proto !== HTMLElement.prototype) {
		if (wrappedProtos.has(proto)) break
		wrappedProtos.add(proto)
		const descs = Object.getOwnPropertyDescriptors(proto)
		for (const [key, desc] of Object.entries(descs)) {
			if (shouldSkipMethod(key)) continue
			if (typeof desc.value !== 'function') continue
			if (!desc.configurable) continue
			const original = desc.value as (...args: unknown[]) => unknown
			Object.defineProperty(proto, key, {
				...desc,
				value: function wrappedForActiveHost(this: HTMLElement, ...args: unknown[]) {
					return _activeHost.run(this, () => original.apply(this, args))
				},
			})
		}
		proto = Object.getPrototypeOf(proto) as object | null
	}
}

const noSignalReads = () => {}

type AnyListener = EventListener | EventListenerObject
const listenerWrapCache = new WeakMap<AnyListener, EventListener>()

function wrapHostListener(host: HTMLElement, listener: AnyListener): EventListener {
	const cached = listenerWrapCache.get(listener)
	if (cached) return cached
	const wrapped: EventListener = function wrappedHostListener(this: unknown, evt: Event) {
		_activeHost.run(host, () => {
			if (typeof listener === 'function') {
				;(listener as EventListener).call(this, evt)
			} else {
				listener.handleEvent(evt)
			}
		})
	}
	listenerWrapCache.set(listener, wrapped)
	return wrapped
}

/**
 * Base class for Schmancy components. A concrete named class — not a mixin
 * factory — so DevTools shows `SchmancyElement` in the prototype chain and
 * `extends SchmancyElement` reads like `extends LitElement` for new
 * contributors.
 *
 * Composes:
 *   1. `LitElement`            — Lit base
 *   2. `BaseElement`           — `disconnecting` Subject, classMap/styleMap,
 *                                 discovery, `stableId`, `uid`, `locale`
 *   3. `SignalWatcher`         — auto-tracks every signal read in `render()`
 *
 * Tailwind styles are injected automatically via `finalizeStyles` — subclasses
 * just declare their own component-local styles in `static styles`:
 *
 *   @schmancyElement('my-card')
 *   class MyCard extends SchmancyElement {
 *     static styles = [css`:host { display: block }`]
 *     render() { return html`<slot></slot>` }
 *   }
 *
 * Cleanup primitives (both fire on disconnect):
 *   - `disconnecting: Subject<void>` — RxJS, used with `takeUntil`
 *   - `disconnectedSignal: AbortSignal` — native, used with `fetch`,
 *     `addEventListener`, or any AbortSignal-aware API
 *
 * Never wrap with `SignalWatcher` again — it is already part of the base.
 * `SignalWatcher(SchmancyElement)` creates two nested Computeds and panics
 * with "Detected cycle in computations" at runtime; the pre-edit lint
 * (`NO_SIGNAL_WATCHER_WRAP`) blocks it as belt-and-suspenders.
 */
export class SchmancyElement extends SchmancyElementBase {
	private abortController = new AbortController()

	/**
	 * AbortSignal that fires when the element disconnects. Per-connection: a
	 * fresh `AbortController` is installed on every (re)connect, so a reparented
	 * or re-added element gets a live, un-aborted signal for its new connection.
	 * Read `this.disconnectedSignal` fresh inside `connectedCallback` (the
	 * convention) and it is always the current connection's signal.
	 */
	get disconnectedSignal(): AbortSignal {
		return this.abortController.signal
	}

	constructor() {
		super()
		wrapPrototypeChain(this.constructor as { prototype: object })
	}

	override connectedCallback() {
		super.connectedCallback()
		// An aborted AbortController never un-aborts, so a reconnect (reparent or
		// genuine re-add) must run against a fresh one — otherwise every
		// `{ signal: this.disconnectedSignal }` call this connection makes would
		// start already-aborted. Only replace after a real disconnect aborted it.
		if (this.abortController.signal.aborted) {
			this.abortController = new AbortController()
		}
	}

	override performUpdate() {
		super.performUpdate()
		if (this.isConnected) return
		// SignalWatcher re-arms its watcher inside EVERY performUpdate, connected
		// or not, but schedules the matching unwatch exactly once — at disconnect.
		// A removal plus a state write in one handler therefore re-arms after that
		// single check has already run, and the detached element stays subscribed
		// to that state for the life of the page. Re-queueing the check after each
		// detached update restores the pairing; deferred rather than immediate for
		// the reason upstream defers its own — a same-task DOM move (repeat(),
		// schmancy-menu adopting slotted content) must keep its watcher.
		queueMicrotask(() => {
			// `updateEffect`'s disposer is the only PUBLIC door to that teardown:
			// the mixin's own `__unwatch`/`_watcher` are property-mangled in the
			// production build, and this path is the one that still honours a
			// `manualDispose` effect the disconnected element is meant to keep.
			if (!this.isConnected) this.updateEffect(noSignalReads)()
		})
	}

	static override finalizeStyles(styles?: CSSResultGroup): CSSResultOrNative[] {
		return [...super.finalizeStyles(styles), tailwindStyles]
	}

	override addEventListener(
		type: string,
		listener: EventListenerOrEventListenerObject | null,
		options?: boolean | AddEventListenerOptions,
	): void {
		if (listener == null) {
			super.addEventListener(type, listener, options)
			return
		}
		super.addEventListener(type, wrapHostListener(this, listener), options)
	}

	override removeEventListener(
		type: string,
		listener: EventListenerOrEventListenerObject | null,
		options?: boolean | EventListenerOptions,
	): void {
		if (listener == null) {
			super.removeEventListener(type, listener, options)
			return
		}
		const wrapped = listenerWrapCache.get(listener)
		super.removeEventListener(type, wrapped ?? (listener as EventListener), options)
	}

	override disconnectedCallback() {
		// Synchronous teardown: this connection's `disconnectedSignal` aborts the
		// moment the element leaves the DOM. A reparent (schmancy-menu adopting
		// slotted content into its overlay, drag-and-drop, any DOM move) fires
		// disconnectedCallback then connectedCallback and pays this teardown, but
		// connectedCallback installs a fresh controller for the new connection —
		// so a fetch/listener scoped to `disconnectedSignal` and re-issued in
		// connectedCallback (the convention) survives the move against the fresh
		// signal, while a genuine removal simply never reconnects. No deferral,
		// no timer, no reconnect-timing race — the signal is per-connection, not one-shot.
		this.abortController.abort()
		super.disconnectedCallback()
	}
}

/**
 * Decorator: `@schmancyElement('tag-name')`. The standard way to register
 * a `SchmancyElement` subclass.
 *
 * Drop-in replacement for Lit's `@customElement`. The difference is
 * timing: this decorator wraps the prototype's methods with
 * `_activeHost.run` BEFORE calling `customElements.define`.
 *
 * Why this matters: the HTML spec captures the element's lifecycle
 * callback references (`connectedCallback`, `disconnectedCallback`, etc.)
 * off the prototype at `customElements.define` time, and the browser
 * invokes those cached references for the element's lifecycle — it does
 * NOT re-look-up the prototype each time. SchmancyElement's constructor
 * installs the active-host wrap on first instantiation, which is strictly
 * later than `define`, so the browser ends up calling unwrapped
 * lifecycle callbacks. Reads inside `connectedCallback` then fall through
 * to `document.activeElement`, miss the enclosing `<schmancy-context>`,
 * and resolve to the module-scoped global instead of the isolated copy.
 *
 * Use this decorator for any subclass that:
 *   - Reads or writes a state-singleton inside `connectedCallback` or
 *     `disconnectedCallback`, AND
 *   - May appear inside a `<schmancy-context>` subtree.
 *
 * For elements that never touch state in lifecycle callbacks, plain
 * `@customElement` from `lit/decorators.js` still works — the
 * constructor's wrap covers everything else (render, class methods,
 * event handlers attached via the prototype). The two decorators are
 * interchangeable for those cases.
 */
export function schmancyElement(tagName: string) {
	return <T extends CustomElementConstructor>(cls: T): T => {
		wrapPrototypeChain(cls as { prototype: object })
		customElements.define(tagName, cls)
		return cls
	}
}
